/**
 * Agent-run and approval vocabulary for the Agents pages: which statuses need
 * the owner, which are finished, and how each kind reads in the UI.
 */
import { humanize } from './format.js';
import type { AgentRunStatus, ApprovalKind, Risk } from './types.js';

export const TERMINAL: ReadonlySet<AgentRunStatus> = new Set(['done', 'failed', 'cancelled', 'blocked']);
export const AWAITING: ReadonlySet<AgentRunStatus> = new Set(['awaiting_plan_approval', 'awaiting_ship_approval']);

export type RunGroup = 'needs_you' | 'running' | 'finished';

export function runGroup(status: AgentRunStatus): RunGroup {
  if (AWAITING.has(status)) return 'needs_you';
  return TERMINAL.has(status) ? 'finished' : 'running';
}

export function runTone(status: AgentRunStatus): 'muted' | 'warn' | 'bad' | 'good' | 'accent' {
  if (status === 'done') return 'good';
  if (status === 'failed' || status === 'blocked') return 'bad';
  if (status === 'cancelled') return 'muted';
  return AWAITING.has(status) ? 'warn' : 'accent';
}

export const APPROVAL_KIND_LABEL: Record<ApprovalKind, string> = {
  agent_plan: 'Plan',
  agent_ship: 'Ship',
  reply_draft: 'Reply draft',
  capture_review: 'Capture review',
  policy_change: 'Policy change',
  other: 'Other',
};

export const RISK_TONE: Record<Risk, 'muted' | 'warn' | 'bad'> = { low: 'muted', medium: 'warn', high: 'bad' };

/** The seeded `agent_policies` task types (spec §3.7), offered when starting a run. */
export const TASK_TYPES: readonly string[] = [
  'copy_edit',
  'image_swap',
  'new_page',
  'client_reply',
  'hours_price_update',
  'money_anything',
  'deploy_fix',
];

/**
 * The words for each seeded task type. The keys are the `agent_policies`
 * task types; the server's `taskLabel` (agents/lifecycle.ts) uses the same
 * words, so a proposal title and a policy row agree.
 */
export const TASK_TYPE_LABEL: Readonly<Record<string, string>> = {
  copy_edit: 'Copy edit',
  image_swap: 'Image swap',
  new_page: 'New page',
  client_reply: 'Client reply',
  hours_price_update: 'Hours or prices',
  money_anything: 'Anything involving money',
  deploy_fix: 'Deploy fix',
};

/** 'hours_price_update' → 'Hours or prices'; an unknown type is humanized. */
export function taskTypeLabel(key: string): string {
  return TASK_TYPE_LABEL[key] ?? humanize(key);
}

/** Hard-stop reason words; mirrors REASON_LABEL in server/src/agents/lifecycle.ts. */
export const HARD_STOP_LABEL: Readonly<Record<string, string>> = {
  money: 'money',
  delete_data: 'deletes data',
  dns: 'DNS',
  new_recipient: 'new recipient',
  ads: 'ads',
  prod_database: 'production database',
  credentials: 'credentials',
  secrets: 'secrets',
  third_party_code: 'third-party code',
  unverified_sender: 'unverified sender',
};

/** 'third_party_code' → 'third-party code'; an unknown reason has its underscores spaced. */
export function hardStopLabel(reason: string): string {
  return HARD_STOP_LABEL[reason] ?? reason.replace(/_/g, ' ');
}

/** Approval payload fields worth showing, read defensively (payload is free-form jsonb). */
export function approvalFacts(payload: Record<string, unknown>): {
  hardStop: boolean;
  reasons: string[];
  commitSha: string | null;
  diffStat: string | null;
  stagingUrl: string | null;
  /** viaMcp: proposed through an MCP client (`source:'mcp'`, from_handle `mcp:<label>`). */
  proposal: { text: string; from: string | null; verified: boolean; viaMcp: boolean } | null;
} {
  const str = (v: unknown): string | null => (typeof v === 'string' && v.trim() !== '' ? v : null);
  const p = payload.proposal;
  const proposal =
    p && typeof p === 'object' && typeof (p as Record<string, unknown>).untrusted_text === 'string'
      ? {
          text: (p as Record<string, unknown>).untrusted_text as string,
          from: str((p as Record<string, unknown>).from_handle),
          verified: (p as Record<string, unknown>).sender_verified === true,
          viaMcp: (p as Record<string, unknown>).source === 'mcp',
        }
      : null;
  return {
    hardStop: payload.hardStop === true,
    reasons: Array.isArray(payload.reasons) ? payload.reasons.filter((r): r is string => typeof r === 'string') : [],
    commitSha: str(payload.commitSha),
    diffStat: str(payload.diffStat),
    stagingUrl: str(payload.stagingUrl),
    proposal,
  };
}

const LOG_TYPE_LABEL: Readonly<Record<string, string>> = {
  created: 'Created',
  runner: 'Runner',
  status: 'Status',
  request_changes: 'Changes requested',
  plan_changes: 'Plan sent back',
  ship_changes: 'Ship sent back',
};

const SOURCE_LABEL: Readonly<Record<string, string>> = { web: 'the web', proposal: 'a proposal', signal: 'Signal', mcp: 'Claude' };

const str = (v: unknown): string | null => (typeof v === 'string' && v.trim() !== '' ? v : null);

/**
 * A run log entry as one readable line. The server writes (agents/store.ts
 * `logEntry`): `{type:'status', from, to, by, reason?}`,
 * `{type:'runner', msg}`, `{type:'created', source, by, approval?}` and
 * `{type:'request_changes'|'plan_changes'|'ship_changes', note}`. Anything
 * else is its humanized type plus whatever text it carries.
 */
export function logLine(entry: unknown): { at: string | null; text: string } {
  if (!entry || typeof entry !== 'object') return { at: null, text: String(entry) };
  const e = entry as Record<string, unknown>;
  const at = typeof e.at === 'string' ? e.at : null;
  const type = str(e.type) ?? str(e.status);
  const by = str(e.by);
  const reason = str(e.reason);

  if (type === 'status' && (str(e.to) || str(e.from))) {
    const to = str(e.to);
    let text = `${humanize(str(e.from) ?? 'start')} → ${to ? humanize(to) : '?'}`;
    if (by) text += ` · ${by}`;
    if (reason) text += ` — ${reason}`;
    return { at, text };
  }
  if (type === 'created') {
    const source = str(e.source);
    const approval = str(e.approval);
    const parts = [source ? `Created from ${SOURCE_LABEL[source] ?? source}` : 'Created'];
    if (approval) parts.push(approval);
    if (by) parts.push(by);
    return { at, text: parts.join(' · ') };
  }

  const body = [e.msg, e.message, e.note, e.detail, e.text, e.reason].map(str).find((v): v is string => v !== null);
  const label = type ? (LOG_TYPE_LABEL[type] ?? humanize(type)) : null;
  if (label && body) return { at, text: `${label}: ${body}` };
  if (label || body) return { at, text: (label ?? body)! };
  const { at: _at, ...rest } = e;
  void _at;
  return { at, text: JSON.stringify(rest) };
}

/**
 * What an approval's summary adds beyond the facts the card already shows
 * (project, risk, level, diff stat, the hard-stop note), or null when it adds
 * nothing. Server summaries are ' · '-joined facts ("Harbor Light · risk
 * medium · 3 files"); only the parts not shown elsewhere are kept.
 */
export function approvalSummary(
  summary: string | null,
  shown: { project: string | null; risk: string; diffStat: string | null; hardStop: boolean },
): string | null {
  if (!summary?.trim()) return null;
  const project = shown.project?.trim().toLowerCase() ?? null;
  const risk = shown.risk.toLowerCase();
  const diff = shown.diffStat?.trim().toLowerCase() ?? null;
  const kept = summary
    .split(' · ')
    .map((p) => p.trim())
    .filter((p) => {
      const l = p.toLowerCase();
      if (l === '') return false;
      if (project !== null && l === project) return false;
      if (l === `risk ${risk}` || l === `${risk} risk`) return false;
      if (/^level \d$/.test(l) || /^l\d$/.test(l)) return false;
      if (diff !== null && l === diff) return false;
      if (shown.hardStop && l.startsWith('hard stop')) return false;
      return true;
    });
  return kept.length > 0 ? kept.join(' · ') : null;
}
